import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { RevealText } from "@/components/shared/RevealText";
import { SectionReveal } from "@/components/shared/SectionReveal";
import { IMAGES } from "@/data/images";
import apiClient from "@/lib/api";

export default function Press() {
  const { t } = useTranslation();
  const releases = t("press.releases", { returnObjects: true });
  const videos = t("press.videos", { returnObjects: true });
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/press/contact", form);
      setSent(true);
      toast.success(t("common.success_generic"));
    } catch {
      toast.error(t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="press-page">
      <SEO title={`${t("press.hero_title")} — Fastority`} description={t("press.hero_subtitle")} path="press" />

      <section className="bg-deepblue text-white py-24 sm:py-28 grain-overlay relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <RevealText lines={[t("press.hero_title")]} />
          </h1>
          <p className="mt-6 text-lg text-white/70">{t("press.hero_subtitle")}</p>
        </div>
      </section>

      <section className="py-20 bg-offwhite">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionReveal className="bg-white rounded-3xl p-8 sm:p-10 border border-gray-100 shadow-[0_8px_30px_rgba(30,58,138,0.06)]">
            <div className="flex flex-col sm:flex-row items-center gap-8 mb-8">
              <img src={IMAGES.logo} alt="Fastority media kit" className="w-24 h-24 object-contain shrink-0" />
              <div className="flex-1">
                <h2 className="font-heading font-bold text-2xl text-slate900">{t("press.kit_title")}</h2>
                <p className="text-slate-600 mt-2">{t("press.kit_subtitle")}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { key: "logo", label: t("press.kit_items.logo"), url: IMAGES.logo },
                { key: "sos_capture", label: t("press.kit_items.sos_capture"), url: IMAGES.mockupSos },
                { key: "map_capture", label: t("press.kit_items.map_capture"), url: IMAGES.mockupMap },
                { key: "alert_capture", label: t("press.kit_items.alert_capture"), url: IMAGES.mockupAlert },
              ].map((asset) => (
                <a
                  key={asset.key}
                  href={asset.url}
                  download
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`press-kit-download-${asset.key.replace(/_/g, "-")}`}
                  className="inline-flex items-center justify-between gap-2 bg-offwhite border border-gray-200 text-slate900 font-medium px-5 py-3 rounded-xl hover:border-coral transition-colors text-sm"
                >
                  {asset.label} <Download size={15} />
                </a>
              ))}
            </div>
          </SectionReveal>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="press-videos-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <SectionReveal className="mb-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate900">{t("press.videos_title")}</h2>
            <p className="mt-2 text-slate-600">{t("press.videos_subtitle")}</p>
          </SectionReveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {videos.map((v, i) => (
              <SectionReveal key={v.title} delay={i * 0.08}>
                <div data-testid={`press-video-${i}`} className="bg-offwhite rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="aspect-video">
                    {v.video_url ? (
                      <video
                        className="w-full h-full object-cover"
                        src={v.video_url}
                        controls
                        preload="none"
                        title={v.title}
                        data-testid={`press-video-native-${i}`}
                      />
                    ) : (
                      <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${v.youtube_id}`}
                        title={v.title}
                        frameBorder="0"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid={`press-video-iframe-${i}`}
                      />
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-sm text-slate900">{v.title}</h3>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-offwhite">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <SectionReveal className="mb-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate900">{t("press.releases_title")}</h2>
          </SectionReveal>
          <div className="space-y-5">
            {releases.map((r, i) => (
              <SectionReveal key={r.title} delay={i * 0.08}>
                <div data-testid={`press-release-${i}`} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <span className="text-xs uppercase tracking-wide font-bold text-coral">{r.date}</span>
                  <h3 className="font-heading font-bold text-lg text-slate900 mt-1 mb-1">{r.title}</h3>
                  <p className="text-sm text-slate-600">{r.excerpt}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-xl mx-auto px-5 sm:px-8">
          <SectionReveal className="text-center mb-8">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate900">{t("press.contact_title")}</h2>
            <p className="mt-2 text-slate-600">{t("press.contact_subtitle")}</p>
          </SectionReveal>

          {sent ? (
            <div className="text-center bg-emeraldgreen/10 border border-emeraldgreen/30 rounded-3xl p-10">
              <CheckCircle2 className="mx-auto mb-3 text-emeraldgreen" size={32} />
              <p className="font-heading font-bold text-slate900" data-testid="press-form-success">{t("common.success_generic")}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="press-contact-form" className="bg-white rounded-3xl p-8 border border-gray-100 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="press-form-name" placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
                <input required type="email" data-testid="press-form-email" placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              </div>
              <input data-testid="press-form-organization" placeholder={t("common.organization")} value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              <textarea required data-testid="press-form-message" placeholder={t("common.message")} rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              <button type="submit" disabled={loading} data-testid="press-form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-slate900 text-white font-semibold px-6 py-4 rounded-full hover:bg-coral transition-colors duration-300 disabled:opacity-60">
                {loading ? t("common.sending") : t("common.send")} <Send size={16} />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
